/**
 * WaypointService - Pure business logic for waypoint operations
 * 
 * This service contains waypoint manipulation logic without React dependencies.
 * It handles coordinate transformations, validation, and batch operations.
 */

import { convertWaypointsToWorldCoords } from '../utils/coordinateTransforms.js';

// Import types for JSDoc
// eslint-disable-next-line no-unused-vars
import { Types } from '../types/index.js';

/**
 * @typedef {Object} FormData
 * @property {string} posX - X position in mm
 * @property {string} posY - Y position in mm
 * @property {string} posZ - Z position in mm
 * @property {string} rotRX - RX rotation in degrees
 * @property {string} rotRY - RY rotation in degrees
 * @property {string} rotRZ - RZ rotation in degrees
 */

/**
 * Convert form data (mm/degrees) to internal units (meters/radians)
 * @param {FormData} formData - Form input data
 * @returns {{position: Types.Position, rotation: Types.Rotation, error: string|null}}
 */
export function parseFormData(formData) {
  const position = {
    x: parseFloat(formData.posX) / 1000,
    y: parseFloat(formData.posY) / 1000,
    z: parseFloat(formData.posZ) / 1000,
  };

  const rotation = {
    rx: parseFloat(formData.rotRX) * Math.PI / 180,
    ry: parseFloat(formData.rotRY) * Math.PI / 180,
    rz: parseFloat(formData.rotRZ) * Math.PI / 180,
  };

  // Validate
  if (isNaN(position.x) || isNaN(position.y) || isNaN(position.z) ||
      isNaN(rotation.rx) || isNaN(rotation.ry) || isNaN(rotation.rz)) {
    return { position: null, rotation: null, error: 'Invalid numeric values' };
  }

  return { position, rotation, error: null };
}

/**
 * Convert internal units (meters/radians) to display units (mm/degrees)
 * @param {Position} position - Position in meters
 * @param {Rotation} rotation - Rotation in radians
 * @returns {{posX: string, posY: string, posZ: string, rotRX: string, rotRY: string, rotRZ: string}}
 */
export function toDisplayUnits(position, rotation) {
  return {
    posX: (position.x * 1000).toFixed(3),
    posY: (position.y * 1000).toFixed(3),
    posZ: (position.z * 1000).toFixed(3),
    rotRX: (rotation.rx * 180 / Math.PI).toFixed(3),
    rotRY: (rotation.ry * 180 / Math.PI).toFixed(3),
    rotRZ: (rotation.rz * 180 / Math.PI).toFixed(3),
  };
}

/**
 * Update a waypoint's position and rotation
 * @param {Array} waypoints - Current waypoints array
 * @param {number} index - Index of waypoint to update
 * @param {Position} position - New position
 * @param {Rotation} rotation - New rotation
 * @returns {Array} Updated waypoints array
 */
export function updateWaypointCoords(waypoints, index, position, rotation) {
  const updatedWaypoints = [...waypoints];
  updatedWaypoints[index] = {
    ...updatedWaypoints[index],
    position,
    rotation,
  };
  return updatedWaypoints;
}

/**
 * Update a waypoint's via-point position and rotation
 * @param {Array} waypoints - Current waypoints array
 * @param {number} index - Index of waypoint
 * @param {Position} position - New via-point position
 * @param {Rotation} rotation - New via-point rotation
 * @returns {Array} Updated waypoints array
 */
export function updateViaPointCoords(waypoints, index, position, rotation) {
  const updatedWaypoints = [...waypoints];
  if (updatedWaypoints[index].viaPoint) {
    updatedWaypoints[index] = {
      ...updatedWaypoints[index],
      viaPoint: {
        ...updatedWaypoints[index].viaPoint,
        position,
        rotation,
      },
    };
  }
  return updatedWaypoints;
}

/**
 * Update a waypoint's joint positions
 * @param {Array} waypoints - Current waypoints array
 * @param {number} index - Index of waypoint to update
 * @param {Array<number>} jointPositions - New joint positions
 * @returns {Array} Updated waypoints array
 */
export function updateWaypointJoints(waypoints, index, jointPositions) {
  const updatedWaypoints = [...waypoints];
  updatedWaypoints[index] = {
    ...updatedWaypoints[index],
    jointPositions,
  };
  return updatedWaypoints;
}

/**
 * Prepare waypoints for visualization (convert to world coordinates)
 * @param {Array} waypoints - Waypoints in feature-local coordinates
 * @param {Array} features - Available features
 * @returns {Array} Waypoints in world coordinates
 */
export function prepareForVisualization(waypoints, features) {
  return convertWaypointsToWorldCoords(waypoints, features);
}

/**
 * Calculate offset between two positions
 * @param {Position} from - Starting position
 * @param {Position} to - Ending position
 * @returns {Position} Offset vector
 */
export function calculateOffset(from, to) {
  return {
    x: to.x - from.x,
    y: to.y - from.y,
    z: to.z - from.z,
  };
}

/**
 * Apply offset to a position
 * @param {Position} position - Original position
 * @param {Position} offset - Offset to apply
 * @returns {Position} New position
 */
export function applyOffset(position, offset) {
  return {
    x: position.x + offset.x,
    y: position.y + offset.y,
    z: position.z + offset.z,
  };
}

/**
 * Scale a position
 * @param {Position} position - Original position
 * @param {number} scale - Scale factor
 * @returns {Position} Scaled position
 */
export function scalePosition(position, scale) {
  return {
    x: position.x * scale,
    y: position.y * scale,
    z: position.z * scale,
  };
}

/**
 * Calculate distance between two positions
 * @param {Position} a - First position
 * @param {Position} b - Second position
 * @returns {number} Euclidean distance
 */
export function calculateDistance(a, b) {
  return Math.sqrt(
    Math.pow(b.x - a.x, 2) +
    Math.pow(b.y - a.y, 2) +
    Math.pow(b.z - a.z, 2)
  );
}

/**
 * Validate waypoint data
 * @param {Object} waypoint - Waypoint to validate
 * @returns {{valid: boolean, errors: Array<string>}}
 */
export function validateWaypoint(waypoint) {
  const errors = [];
  
  if (!waypoint.position) {
    errors.push('Missing position');
  } else {
    if (typeof waypoint.position.x !== 'number' || isNaN(waypoint.position.x)) {
      errors.push('Invalid position.x');
    }
    if (typeof waypoint.position.y !== 'number' || isNaN(waypoint.position.y)) {
      errors.push('Invalid position.y');
    }
    if (typeof waypoint.position.z !== 'number' || isNaN(waypoint.position.z)) {
      errors.push('Invalid position.z');
    }
  }
  
  if (!waypoint.rotation) {
    errors.push('Missing rotation');
  } else {
    if (typeof waypoint.rotation.rx !== 'number' || isNaN(waypoint.rotation.rx)) {
      errors.push('Invalid rotation.rx');
    }
    if (typeof waypoint.rotation.ry !== 'number' || isNaN(waypoint.rotation.ry)) {
      errors.push('Invalid rotation.ry');
    }
    if (typeof waypoint.rotation.rz !== 'number' || isNaN(waypoint.rotation.rz)) {
      errors.push('Invalid rotation.rz');
    }
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Batch update multiple waypoints with an operation
 * @param {Array} waypoints - Current waypoints
 * @param {Array<number>} indices - Indices to update
 * @param {Function} operation - Function to apply to each waypoint (wp, index) => newWp
 * @returns {Array} Updated waypoints array
 */
export function batchUpdateWaypoints(waypoints, indices, operation) {
  const updatedWaypoints = [...waypoints];
  
  for (const index of indices) {
    if (index >= 0 && index < updatedWaypoints.length) {
      updatedWaypoints[index] = operation(updatedWaypoints[index], index);
    }
  }
  
  return updatedWaypoints;
}

/**
 * Filter waypoints by feature
 * @param {Array} waypoints - All waypoints
 * @param {string} featureName - Feature name to filter by
 * @returns {Array<{waypoint: Object, index: number}>} Filtered waypoints with indices
 */
export function filterByFeature(waypoints, featureName) {
  return waypoints
    .map((waypoint, index) => ({ waypoint, index }))
    .filter(({ waypoint }) => waypoint.feature === featureName);
}

/**
 * Get waypoint statistics
 * @param {Array} waypoints - Waypoints to analyze
 * @returns {Object} Statistics object
 */
export function getWaypointStats(waypoints) {
  if (!waypoints || waypoints.length === 0) {
    return {
      count: 0,
      features: [],
      hasViaPoints: 0,
      bounds: null
    };
  }
  
  const features = [...new Set(waypoints.map(wp => wp.feature))];
  const hasViaPoints = waypoints.filter(wp => wp.viaPoint).length;
  
  const bounds = {
    min: { x: Infinity, y: Infinity, z: Infinity },
    max: { x: -Infinity, y: -Infinity, z: -Infinity }
  };
  
  for (const wp of waypoints) {
    bounds.min.x = Math.min(bounds.min.x, wp.position.x);
    bounds.min.y = Math.min(bounds.min.y, wp.position.y);
    bounds.min.z = Math.min(bounds.min.z, wp.position.z);
    bounds.max.x = Math.max(bounds.max.x, wp.position.x);
    bounds.max.y = Math.max(bounds.max.y, wp.position.y);
    bounds.max.z = Math.max(bounds.max.z, wp.position.z);
  }
  
  return {
    count: waypoints.length,
    features,
    hasViaPoints,
    bounds
  };
}
